import { useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

// Lets an admin attach a timesheet to the correct employee; the backend recalculates
// wages and loaded cost from that employee's rates.
export function AssignEmployeeDialog({ open, onOpenChange, timesheet, bulkIds, employees = [], onAssigned }) {
  const [eid, setEid] = useState("");
  const [busy, setBusy] = useState(false);
  const active = employees.filter((e) => e.active !== false);
  const bulk = Array.isArray(bulkIds) && bulkIds.length > 0;
  const save = async () => {
    if (!eid) { toast.error("Pick an employee"); return; }
    setBusy(true);
    try {
      if (bulk) {
        const { data } = await api.post(`/timesheets/bulk-assign-employee`, { ids: bulkIds, employee_id: eid });
        toast.success(`Assigned ${data.done}${data.skipped ? ` · ${data.skipped} skipped` : ""} — pay & cost recalculated`);
      } else {
        if (!timesheet?.id) return;
        await api.post(`/timesheets/${timesheet.id}/assign-employee`, { employee_id: eid });
        toast.success("Timesheet linked — pay & cost recalculated");
      }
      onAssigned?.(); onOpenChange(false); setEid("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setEid(""); onOpenChange(v); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{bulk ? `Assign ${bulkIds.length} timesheet${bulkIds.length > 1 ? "s" : ""} to one employee` : "Assign to employee"}</DialogTitle>
          <DialogDescription>Pick the staff member {bulk ? "these timesheets belong" : "this timesheet belongs"} to. Their pay rates are used to recalculate wages and loaded cost. Locked/exported timesheets are skipped.</DialogDescription>
        </DialogHeader>
        {!bulk && timesheet?.unlinked && (
          <div data-testid="assign-loggedby" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {timesheet.logged_by_name ? (
              <>
                <div>Logged by <span className="font-semibold">{timesheet.logged_by_name}</span>{timesheet.logged_by_email ? ` (${timesheet.logged_by_email})` : ""}</div>
                <div className="text-amber-700">{timesheet.logged_by_method === "import" ? "from import" : "from entry history"}{timesheet.logged_at ? ` · ${new Date(timesheet.logged_at).toLocaleDateString("en-GB")}` : ""}</div>
                {Array.isArray(timesheet.logged_by_candidates) && timesheet.logged_by_candidates.length > 1 && (
                  <div className="text-amber-700 mt-1">Other possible: {timesheet.logged_by_candidates.slice(1).map((c) => c.name).join(", ")}</div>
                )}
              </>
            ) : (!timesheet.import_ref && <div>Logger unknown — use the sites, week and hours below to identify the worker.</div>)}
            {timesheet.import_ref && <div className="mt-1">Import identifier: <span className="font-semibold">{timesheet.import_ref}</span></div>}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label>Employee</Label>
            <Select value={eid} onValueChange={setEid}>
              <SelectTrigger data-testid="assign-emp-select" className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{active.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`assign-emp-opt-${e.id}`}>{e.first_name} {e.surname}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="assign-emp-save" onClick={save} disabled={busy} className="w-full bg-slate-900 text-white">{busy ? "Saving…" : "Assign & recalculate"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
